import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  BookOpen,
  CreditCard,
  ShieldCheck,
  Wrench,
  Loader2,
  Check,
  LifeBuoy,
  Lightbulb,
  Image as ImageIcon,
  Send,
  Megaphone,
  Code2,
  Download,
  Share2,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting started",
    icon: BookOpen,
    faqs: [
      {
        q: "How do I get around — especially on my phone?",
        a: "On a phone, the bar along the bottom has Study, Voice (your Voice Notes), the central \"Go Deeper\" button, Prayers, and \"More.\" Tap \"More\" to reach everything else — Study together, My Studies, Search, Resources, Settings, Feedback and Help & Support. On a computer, those same links live in the left sidebar. Every feature is available on both phone and desktop.",
      },
      {
        q: "How do I start a study?",
        a: "Open the Study tab and type a passage (e.g. \"John 3\" or \"Psalm 23\") or a topic. Choose to study with the virtual study companion or on your own, then begin. OpenWord AI always brings you back to the text of Scripture first.",
      },
      {
        q: "What is \"Go Deeper\"?",
        a: "Go Deeper is the virtual study companion. It never lectures first — it asks what you notice, then walks through the passage with you, always holding everything up against Scripture. You can open it from inside a study or from the Study home screen.",
      },
      {
        q: "Can I listen to a study instead of reading it?",
        a: "Yes — and it now uses a warm, natural-sounding voice. Every study has a \"Listen to this study\" button near the top that reads the whole thing aloud (passage, teaching and reflections) with play, pause, resume and a progress bar. Each section has its own speaker button, and every Go Deeper answer can be listened to as well.",
      },
      {
        q: "Read along with the words highlighted",
        a: "Next to the listen button, tap \"Read along\" to open a clean, full-screen view that plays the study aloud and highlights each sentence as it's read, scrolling automatically so you never lose your place. It's designed for anyone who prefers to listen, is still learning to read, or just wants to follow along.",
      },
      {
        q: "Choose a voice and reading speed",
        a: "Tap the small settings (gear) icon beside the listen button to choose a narrator voice and set the reading speed from 0.75× up to 1.5×. Everyone gets a set of free voices. Pro and Team members get an extra set of more natural, expressive Premium voices in that same picker — if you're on Free or Basic you won't see that Premium section (or if it's ever briefly unavailable, playback quietly uses a free voice instead — it never breaks). Your choice is remembered for next time.",
      },
      {
        q: "Can I record a thought instead of typing it?",
        a: "Yes — open Voice Notes and tap the mic. It's transcribed and tidied into a titled note automatically, free. From any note you can \"Explore as a study topic\" for free, or \"Generate a full study\" on Pro and above (not available on Free or Basic).",
      },
      {
        q: "Can I get a simpler version of a study?",
        a: "Yes. Every study has a Simple / Standard reading-level toggle near the top. \"Simple\" rewrites the teaching in plainer, shorter language for a first-time reader; \"Standard\" is the fuller depth. Switching is free and instant — it never costs credits.",
      },
      {
        q: "How do the original-language (Strong's) words work?",
        a: "Original-language words are tappable — both inline in the teaching and in the word-study list. Tapping opens the real, sourced Strong's entry: the original word, transliteration, pronunciation, definition and how the KJV renders it. You'll also see a \"Word Trail\" (how many times the word appears in Scripture and where) and an \"Author fingerprint\" (which biblical writers use it most). This is canonical concordance data — not AI-guessed.",
      },
      {
        q: "Does it remember what I've studied before?",
        a: "Yes — every study you do is saved automatically under My Studies, so you can reopen, rename, copy or share any of them any time. There are no streaks, points or badges — just your studies, kept.",
      },
      {
        q: "What is \"Study together\" (live study rooms)?",
        a: "A free, real-time shared study for a small group. Anyone with an account — on ANY plan, including Free — can create a room or join one with a code; there's no subscription required to participate and it never uses study credits. Everyone in the room sees the host's chosen passage stay in sync, there's a group chat, and shared notes everyone can edit together. The host can turn a shared AI study companion on or off for the whole group; when it's on, anyone can ask it a question and everyone sees the answer together. The host can end the room any time — after that it turns read-only.",
      },
      {
        q: "What are \"Related passages\"?",
        a: "Below the Strong's word study on any finished study, a \"Related passages\" section suggests other passages connected to what you're reading — free, on any plan. Tap one to open it as a brand-new study.",
      },
      {
        q: "What is a \"Sermon outline\"?",
        a: "On Pro and above, generate a preachable outline — a clear big idea and the movements the passage itself calls for, with application and a closing — using study credits. You can start one from a study's Tools tab, a \"Turn this into a sermon\" button on a Go Deeper conversation, or a \"Turn into a sermon\" button on a Prayer Journal entry. Edit it before sharing with one-tap social buttons and a custom preview image.",
      },
      {
        q: "What is \"Teach & Share\"?",
        a: "Once you've gone deep enough on a study (3+ Go Deeper exchanges), a Teach & Share section appears below the study — available on EVERY plan, not just paid ones. Pick a format (Sermon Outline, Teaching/Small Group Notes, Discussion Guide, Devotional, or a Short Teaching Nugget — each shows its study-credit cost up front), answer three short reflection questions in your own words, and it builds an editable draft you can reorder, edit, copy, save, or share as a public link with a preview image and a downloadable PDF. Your past drafts are kept in your own Teach & Share gallery.",
      },
      {
        q: "What is \"Passage history\"?",
        a: "If you've studied the same passage more than once, its Passage history page (reachable from that passage in My Studies) gathers every visit in one place, chronologically — with \"Copy all visits\" and \"Export all (PDF)\" to combine everything you've drawn from it into a single document.",
      },
      {
        q: "What's on the Resources page?",
        a: "A short, static guide — a 5-step \"How to use OpenWord AI\" walkthrough, plus a plain-language guide to every Bible translation the app offers. Reach it from \"More\" on a phone, or the sidebar on a computer.",
      },
      {
        q: "What's the \"What's new\" section in Settings?",
        a: "A running changelog of recent app updates — open Settings and tap \"What's new\" to see what's changed lately.",
      },
      {
        q: "Study Alone vs. with the companion?",
        a: "In Settings, \"Default study mode\" lets you choose whether new studies start with the virtual study companion available, or in a quieter \"Study alone\" mode with just the text and word study. You can switch it on any specific study too.",
      },
      {
        q: "What is the Prayer Journal?",
        a: "Any prayer you write yourself and turn into a short guided study (Prayer-to-Study) is gathered under the Prayers tab, in your own words. OpenWord AI never writes, offers or leads a prayer — prayer is yours to bring to God in your own words.",
      },
      {
        q: "What are Voice Notes?",
        a: "Caught a thought with no time to write it down? Open the Voice Notes tab and tap the microphone to record. We transcribe it and tidy it into a titled note — with a short summary and a few tags — automatically. Recording and organising your notes is free.",
      },
      {
        q: "What can I do with a voice note?",
        a: "From any note you can tap \"Explore as a study topic\" to start a study from the idea (free), or \"Generate a full study\" (on Pro). Notes you haven't acted on yet show a gentle reminder at the top of the page, and a note you've turned into a study gets an \"Explored\" badge so you can see at a glance what's still waiting. Each note also has audio playback.",
      },
      {
        q: "Can I change my Bible translation?",
        a: "New studies use the NIV by default. To read another version, open Settings and pick your default translation (NIV, WEB, KJV, ASV, NKJV or AMP), or tap the version label on any study page to switch just that study. OpenWord AI only falls back to a different version if the NIV is temporarily unavailable.",
      },
      {
        q: "Can I copy part of a study — not just the whole thing?",
        a: "Yes. Every part of a study has its own copy button: the overview, each teaching section, the summary and the exercise. The study's copy menu (the \"…\" actions) also has \"All teaching sections\" to grab the whole teaching in one tap, alongside \"Whole study\", \"Scripture only\" and \"My notes\". Each Strong's word entry has a copy button too, so word studies paste straight into your notes. Everything you copy includes a small link back to OpenWord AI.",
      },
    ],
  },
  {
    id: "sharing",
    title: "Sharing, copying & Go Deeper conversations",
    icon: Share2,
    faqs: [
      {
        q: "Can I share a study to social media?",
        a: "Yes. On any study tap \"Share\" to open the share dialog — it has one-tap buttons for Facebook, X, WhatsApp and LinkedIn, an Instagram caption to copy, email, and \"Copy link\", plus a preview image. Only the study is made public — your notes, reflections and Go Deeper conversations stay private unless you deliberately opt them in.",
      },
      {
        q: "Can I share just one section of a study?",
        a: "Yes. Each teaching section has its own Share icon (next to the copy and listen icons). It creates a public link to just that section — with a branded preview image and the same Facebook / X / WhatsApp / LinkedIn / Instagram / email options. Anyone who opens it sees that section plus a gentle invitation to study the whole passage. You can turn the link off any time.",
      },
      {
        q: "Can I copy or share a Go Deeper conversation on its own?",
        a: "Yes. Open a saved conversation (from My Studies) and use \"Copy conversation\" to copy the whole exchange, or \"Share\" to create a public link and post it to Facebook, X, WhatsApp or LinkedIn (or copy an Instagram caption / email it). The shared page shows the whole conversation — both your questions and the companion's replies.",
      },
      {
        q: "Can I turn a Go Deeper conversation into a full study?",
        a: "Yes. On a saved conversation, tap \"Turn this into a study\" and OpenWord AI builds a full structured study — grounded in the original languages — around what you explored together. It's saved to My Studies like any other study.",
      },
      {
        q: "Can I rename a Go Deeper conversation?",
        a: "Yes. Go Deeper conversations save to My Studies automatically. Open one and tap the pencil next to its title to give it a name that's easy to find later.",
      },
      {
        q: "What gets included when I share or copy?",
        a: "Everything you copy or share carries a small link back to OpenWord AI so friends can pick up where you left off. Public share links only show the content you shared — never your private notes or reflections — and you can turn any share link off whenever you like.",
      },
      {
        q: "Can I share a Reflections (blog) post?",
        a: "Yes. Every Reflections post has one-tap share buttons — Facebook, X, WhatsApp, LinkedIn, an Instagram caption to copy, email, and copy link — at both the top and bottom of the post.",
      },
    ],
  },
  {
    id: "billing",
    title: "Plans, credits & billing",
    icon: CreditCard,
    faqs: [
      {
        q: "What are credits?",
        a: "Reading Scripture, taking notes, your history, the concordance / Word Trails, Related passages and Study together (live rooms) are always free and unlimited — they never cost credits. Credits are only for the deeper AI tools, drawn from one shared monthly pool: a Go Deeper conversation costs 1 credit (one deeper conversation per study per month); a Prayer-to-Study, 2; a Teach & Share Devotional or Short Teaching Nugget, 1; a Teach & Share Discussion Guide or Teaching/Small Group Notes, 3; and a Sermon outline (Tools tab or Teach & Share), 4. The Free plan includes 15 credits a month; paid plans include more.",
      },
      {
        q: "Where do I see my credits?",
        a: "Your remaining credits show in the sidebar (and in the mobile \"More\" menu), and again right next to each deeper tool. The meter turns amber when you're running low. During your free trial it simply reads \"Trial · unmetered.\"",
      },
      {
        q: "What is the free trial?",
        a: "New accounts start on a {days}-day free trial that runs completely unmetered — nothing is charged during the trial and no card is required. When it ends you move to the Free plan (15 credits a month) and everything you've written stays yours. Upgrade anytime to keep creating.",
      },
      {
        q: "How do top-ups work?",
        a: "If you run low, you can buy a one-time top-up of 100, 250 or 600 credits. Top-up credits never expire and stack on top of your monthly credits. Find them under Settings → Manage subscription, or on the Plans page.",
      },
      {
        q: "How do I change or cancel my plan?",
        a: "Go to Settings → Manage subscription → Manage plan. You can switch plans anytime (changes are prorated) or return to Free. If you cancel, your plan stays active until the end of the current billing period, then gently returns to Free — and you're always welcome back.",
      },
      {
        q: "Is my payment secure?",
        a: "Yes. All payments are handled securely by Stripe. We never see or store your card details.",
      },
    ],
  },
  {
    id: "account",
    title: "Account & privacy",
    icon: ShieldCheck,
    faqs: [
      {
        q: "How do I sign in?",
        a: "You can sign in with your email and password or with \"Continue with Google.\" If you forget your password, use the reset link on the login page.",
      },
      {
        q: "Is my study private?",
        a: "Yes. Your studies, notes, prayers and Go Deeper conversations are private to your account. When you choose to share a study, only that content is made public — never your notes, prayers, reflections or conversations.",
      },
      {
        q: "How is my data used?",
        a: "We only use your email to sign you in and follow up on any support message. See our Privacy Policy and Terms for the full detail.",
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: Wrench,
    faqs: [
      {
        q: "A passage won't load.",
        a: "Check the reference format (e.g. \"1 Timothy 5:1-5\") and your connection, then try again. If a specific translation won't load, try another and let us know using the form below.",
      },
      {
        q: "The virtual study companion says it's unavailable.",
        a: "This is usually brief — the virtual study companion is momentarily busy. Wait a little while and try again. If it persists, tell us below.",
      },
      {
        q: "My payment went through but my plan didn't update.",
        a: "Give it a moment and refresh the Plans page — fulfillment can take a few seconds. If your credits or tier still haven't updated, contact us below with the date and amount and we'll sort it out.",
      },
      {
        q: "I didn't get an email.",
        a: "Check your spam or promotions folder first. If it's still missing, reach out below.",
      },
    ],
  },
];

const CATEGORIES = [
  { id: "technical", label: "Technical problem", icon: LifeBuoy },
  { id: "feedback", label: "Feedback or idea", icon: Lightbulb },
];

// Admin-only creator's manual. Plain language, no jargon.
const ADMIN_GUIDE = [
  {
    id: "images",
    title: "AI image generation for posts",
    icon: ImageIcon,
    faqs: [
      {
        q: "What does the image tool do?",
        a: "It creates a original, photorealistic picture to go with a social post — no stock photos, no watermarks. You describe what you want in plain words and it paints it. If image generation is ever unavailable, the post falls back to a clean, branded quote card so you always have something to publish.",
      },
      {
        q: "How do I get a GREAT image (the prompt that matters)",
        a: "Be specific and visual. Name the SUBJECT, the SETTING, the LIGHT, and the MOOD. Weak: \"a Bible.\" Strong: \"an open leather Bible on a worn wooden table by a window at golden hour, soft morning light, shallow depth of field, warm and peaceful.\" Add a style word or two — \"cinematic,\" \"documentary photo,\" \"editorial,\" \"muted tones.\" Avoid text-in-image requests; AI spells badly, so let the quote card handle words.",
      },
      {
        q: "Which shape should I pick?",
        a: "Match the platform. SQUARE (1:1) is the safe all-rounder for Instagram and Facebook feeds. LANDSCAPE (wide) suits X/Twitter, LinkedIn and blog headers. PORTRAIT (tall) is best for Instagram/Facebook Stories, Reels covers and Pinterest. When in doubt, square travels everywhere.",
      },
      {
        q: "Can I edit the prompt and try again?",
        a: "Yes — every image has an editable prompt. Tweak a few words and regenerate until it feels right. Small changes (light, mood, one object) usually do more than rewriting the whole thing. Generating uses credits, so aim your prompt before spamming regenerate.",
      },
      {
        q: "How do I preview before it goes out?",
        a: "Use the Preview step in the Social Queue (see the next section). Always eyeball the image for odd hands, garbled text or anything off-brand before you approve — AI occasionally slips.",
      },
      {
        q: "Text-on-colour verse cards (no photo)",
        a: "Sometimes you just want clean scripture over a solid colour — the Facebook \"text post\" look, no photo. Ask Shee to \"make a verse card for Ephesians 3:20 on a black background\" and she'll create one: bold verse text, the reference beneath it, and the OpenWord AI wordmark, on the colour you choose (teal, black, navy, forest, plum, maroon, slate, charcoal, or a light cream/sand). The exact verse text is pulled from the Bible automatically so it's always accurate. It's staged in the review queue like any post — approve it to publish. Backgrounds pick white or dark text automatically for readability.",
      },
    ],
  },
  {
    id: "media-library",
    title: "Media Library & True Vision",
    icon: ImageIcon,
    faqs: [
      {
        q: "What is the Media Library?",
        a: "A place under Content → Setup to upload your own photos and video clips from your computer, or import them from a link — separate from AI-generated images. Shee can pick from it when drafting posts and Reels, and it now backs Reels footage/avatar uploads too. Uploading the exact same file twice is detected and won't create a duplicate.",
      },
      {
        q: "What is 'True Vision'?",
        a: "Shee can now actually LOOK at a photo or video in your library and describe what's really in it (subjects, setting, mood, colours, on-screen text). Click \"Analyze (True Vision)\" on any item, or ask Shee about it in chat — this can take up to a minute since it's a real AI look, not a guess.",
      },
      {
        q: "Will Shee use my library photos automatically?",
        a: "No — for Reflections posts, Shee always asks first whether you want a library photo or a freshly generated one before deciding. She'll never silently swap a photo without your say-so.",
      },
      {
        q: "Fullscreen preview & filters",
        a: "Click any thumbnail to open it fullscreen (image or playable video). Use the All / Image / Video tabs above the grid to filter, and the trash icon on a card to remove it from the library.",
      },
      {
        q: "Will Shee use my own photo/video, or generate a new one, when I attach one in chat?",
        a: "If you say \"use this photo\" or \"use my image\" about something you just attached, Shee saves it to your library verbatim and attaches that exact file — zero cost, no AI generation. She only generates a fresh AI photo (a paid step) if you ask for a new one, or if you haven't said which you want and she hasn't asked yet. She's instructed to never generate first and \"fix\" it with your file afterwards.",
      },
      {
        q: "Can Shee change or add a photo on a post that's already published?",
        a: "Yes — ask her in chat (e.g. \"swap the photo on the Psalm 23 post for one of my library shots\"). She can reroll or add a photo on a currently-live article exactly like a staged one, no unpublish/republish needed — the live page updates immediately.",
      },
      {
        q: "Why did my Instagram-portrait or square crop used to cut people out of the shot?",
        a: "Crops now use a smart, subject-aware focus point (the busiest/most detailed part of the photo — almost always where the person or subject is) instead of always cropping dead-centre, so square/portrait/landscape versions of the same photo keep the subject in frame instead of slicing them out.",
      },
    ],
  },
  {
    id: "content-dashboard",
    title: "The Content & Social dashboard",
    icon: Megaphone,
    faqs: [
      {
        q: "How is Admin → Content & Social organised now?",
        a: "It's one flat row of tabs — no more nested menus. Chat with Shee (talk to your creative director), Manual (a lightweight composer: Blog post, Social post, Reel, Daily Nugget), Queue (review & publish), Track (analytics), Setup (connections, Reels/Nuggets config, Media Library, Published Articles), FB Comments, FB DMs, and Brand Guidelines. Tap the category, then tap any of these — nothing is hidden a second tap deep, and the row scrolls sideways if your screen isn't wide enough to show all of them.",
      },
      {
        q: "Manual mode — Reel and Daily Nugget",
        a: "In Manual pick 'Reel' to give a brief and a platform — Shee drafts the script and video prompt, then you render and post it from Setup → Reels (voice, avatar and length come from your saved Reel settings there). Pick 'Daily Nugget' to draft a run of short daily reflections ahead of time; review, edit and approve each one under Setup → Daily Nuggets.",
      },
      {
        q: "Shee is more than marketing now",
        a: "Shee is also your strategy partner. Bounce ANY idea off her — new features, the roadmap, pricing, growth, positioning, ministry strategy — and she'll think it through with you, name the risks, and suggest next steps. You don't need to be making a post; you can just have a working conversation. Her replies are no longer cut short.",
      },
      {
        q: "What is Brand Guidelines?",
        a: "A tab where you write (or edit) the house rules for tone, copy, photography and video style — this is injected into Shee's instructions on every single conversation, so she treats it as binding. If you tell Shee in chat to \"remember this as a permanent rule,\" she can append to this doc herself, so a standing preference is never forgotten or contradicted later.",
      },
      {
        q: "Can Shee actually look at a photo or video I attach in chat?",
        a: "Yes — attach an image OR a video directly to a chat message (not just Media Library items) and Shee can genuinely look at it and describe what's in it, when it's useful to the conversation. If you're only attaching a photo as a style reference for a new image (not asking her to describe it), she won't waste a look on it. A chat attachment is a one-off for that conversation — it isn't saved to your Media Library unless you separately ask her to.",
      },
    ],
  },
  {
    id: "facebook-concierge",
    title: "FB Comments & FB DMs — Shee as your Page concierge",
    icon: MessageCircle,
    faqs: [
      {
        q: "What do the FB Comments and FB DMs tabs do?",
        a: "Shee watches the comments and direct messages on your connected Facebook Page and can draft a reply for a genuine question or message. She never posts a reply on her own — every draft is held for your approval in these tabs, so you always see exactly what she'd say before it goes out.",
      },
    ],
  },
  {
    id: "queue-publish",
    title: "Queue — review, approve & see where each post landed",
    icon: Send,
    faqs: [
      {
        q: "How does publishing work?",
        a: "Nothing goes out on its own. Everything you or Shee create lands in the Queue tab as a draft. A row of platform tabs — Blog, Facebook, Instagram, TikTok, LinkedIn, YouTube — lets you filter the list to just one destination; a post aimed at more than one place (e.g. Facebook + Blog) appears under every tab it targets. (YouTube is a placeholder tab for now — the app doesn't publish there yet.) Open an item, set its destinations (Facebook Direct, Blog, and/or PostPulse platforms), Preview it, then Approve (schedule) or Post now.",
      },
      {
        q: "A daily nugget or post is stuck \"pending\" in PostPulse and won't go live — how do I post it?",
        a: "This happens when a post was sent to PostPulse but PostPulse never published it (usually the Facebook connection there went stale). Every submitted item now has two recovery buttons: \"Post directly now\" publishes it straight to your Facebook Page via the app's own connection, bypassing PostPulse entirely — the fastest way to get it live. Or use \"Re-open\" to send it back to a draft so you can change the destination (e.g. switch to Facebook Direct) or edit it, then Post now. Re-opening clears the old stuck PostPulse submission.",
      },
      {
        q: "The Post Status Trail — did it actually send?",
        a: "After you approve a post, each Queue item shows a 'Sent to' row of chips telling you exactly what happened per destination: Facebook · posted, Blog · published, PostPulse · sent (or dry-run if PostPulse isn't connected), and failed if something went wrong. No more guessing whether a post really went live.",
      },
      {
        q: "What is PostPulse and dry-run?",
        a: "PostPulse is the delivery service that pushes approved posts to Instagram, TikTok, LinkedIn, etc. Connect it under Setup. Until it's connected, PostPulse destinations stay in safe 'dry-run' (staged but not sent) — Facebook Direct still posts for real via your connected Page. If a PostPulse post won't send, re-check the connection and that a Page is selected in PostPulse, then 'Refresh accounts'.",
      },
      {
        q: "Setup — connections and Reels/Nuggets config",
        a: "The Setup tab holds your PostPulse connection plus collapsible panels for Reels config (voice, avatar, daily cap), Daily Nuggets config (draft ahead, schedule, subscribers), Media Library, and Published Articles. Creating happens in Chat with Shee or Manual; the knobs live here so they stay out of your way.",
      },
      {
        q: "How do I take down or delete a blog post that's already live?",
        a: "Setup → Published Articles lists every currently-live blog post with its publish date. \"Unpublish\" pulls it off the public site immediately but keeps the content — it's reversible, and you (or Shee, if you ask her) can republish it later. \"Delete\" permanently removes the post and its image after a confirmation step — this can't be undone. Shee can browse published posts and unpublish one for you in chat, but she does NOT have a delete ability — permanent deletion stays a manual, confirmed action only you can do, so a chat conversation can never destroy a live article by mistake. Either action only affects this app's own record — it can't retract a post already pushed out to Facebook/Instagram/etc.",
      },
    ],
  },
  {
    id: "intake-api",
    title: "External content intake API (for your AI agent)",
    icon: Code2,
    faqs: [
      {
        q: "What is the intake API for?",
        a: "It lets an outside AI agent (yours) submit topics and finished posts straight into OpenWord AI's content pipeline — so content can be produced without you touching the dashboard. Submitted items still land in the queue for your preview/approval unless configured otherwise.",
      },
      {
        q: "How is it secured?",
        a: "Every request must include your secret CONTENT_API_KEY. Keep that key private — never paste it into a public place, a screenshot, or a git repo. If it ever leaks, rotate it. Requests without the correct key are rejected.",
      },
      {
        q: "What can it submit?",
        a: "Two things: a TOPIC (an idea for the engine to write about) and a finished POST (ready-made content to enter the queue). Both flow into the same preview-and-approve pipeline so you keep editorial control.",
      },
    ],
  },
];

export default function Help() {
  const { user } = useAuth();
  const [category, setCategory] = useState("technical");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [trialDays, setTrialDays] = useState(7);

  useEffect(() => {
    api.get("/me/subscription")
      .then(({ data }) => setTrialDays(data?.trial?.total_days || 7))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!email.trim()) {
      toast.error("Please add an email so we can reach you");
      return;
    }
    setLoading(true);
    try {
      await api.post("/support", {
        text: text.trim(),
        name: name.trim(),
        email: email.trim(),
        category,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="px-6 py-10 md:max-w-3xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="space-y-2 mb-8" data-testid="help-header">
        <h1 className="text-3xl text-white">Help &amp; Support</h1>
        <p className="text-sm text-slate-400 leading-relaxed">
          Answers to common questions about studying, plans and your account. Can&apos;t find what you
          need? Send us a note at the bottom and we&apos;ll reply.
        </p>
      </header>

      <div className="space-y-8">
        {SECTIONS.map(({ id, title, icon: Icon, faqs }) => (
          <section key={id} data-testid={`help-section-${id}`} className="space-y-3">
            <div className="flex items-center gap-2.5 text-slate-200">
              <Icon className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
              <h2 className="text-xl">{title}</h2>
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border border-[#1E273D] bg-[#0E131F] px-4">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`${id}-${i}`} data-testid={`help-faq-${id}-${i}`} className="border-[#1E273D]">
                  <AccordionTrigger className="text-sm text-left text-slate-300 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-400 leading-relaxed">
                    {f.a.replace("{days}", String(trialDays))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}
      </div>

      {/* Admin-only creator's manual */}
      {user?.role === "admin" && (
        <div className="mt-12" data-testid="admin-guide">
          <div className="rounded-2xl bg-[#141B29] text-white p-6 mb-6">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-2">Admin only</div>
            <h2 className="text-2xl">Creator&apos;s Guide</h2>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              How to get the most out of your content tools — the unified Content dashboard (Shee,
              Manual mode &amp; the Queue), image generation, publishing with the status trail,
              Reels &amp; Daily Nuggets, and the intake API. Written in plain language.
            </p>
            <a
              href="/openword-creator-guide.md"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-guide-download"
              className="inline-flex items-center gap-2 mt-4 bg-[#0E131F]/10 hover:bg-[#0E131F]/20 transition-colors rounded-full px-4 py-2 text-sm"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} /> Download printable version
            </a>
          </div>

          <div className="space-y-8">
            {ADMIN_GUIDE.map(({ id, title, icon: Icon, faqs }) => (
              <section key={id} data-testid={`admin-guide-section-${id}`} className="space-y-3">
                <div className="flex items-center gap-2.5 text-slate-200">
                  <Icon className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
                  <h3 className="text-xl">{title}</h3>
                </div>
                <Accordion type="single" collapsible className="rounded-2xl border border-[#1E273D] bg-[#0E131F] px-4">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`${id}-${i}`} data-testid={`admin-guide-faq-${id}-${i}`} className="border-[#1E273D]">
                      <AccordionTrigger className="text-sm text-left text-slate-300 hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-400 leading-relaxed">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}
          </div>
        </div>
      )}

      {/* Contact form */}
      <section className="mt-12 rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6" data-testid="help-contact">
        <h2 className="text-xl text-white mb-1">Still need help?</h2>
        <p className="text-sm text-slate-400 mb-6 leading-relaxed">
          Report a problem or share an idea. We&apos;ll email you an update once it&apos;s resolved — no
          need to chase anyone down.
        </p>

        {sent ? (
          <div
            data-testid="help-success"
            className="rounded-xl bg-[#0E131F] border border-[#00E5FF]/15 p-6 text-center space-y-3"
          >
            <div className="w-12 h-12 rounded-full bg-[#00E5FF] flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <p className="text-slate-300 leading-relaxed">
              Thank you — we&apos;ve received this. We&apos;ve sent a confirmation to your email and will
              let you know as soon as it&apos;s resolved.
            </p>
            <Button
              data-testid="help-submit-another"
              onClick={() => {
                setSent(false);
                setText("");
              }}
              variant="outline"
              className="rounded-full border-[#29344D]"
            >
              Submit another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase tracking-widest text-slate-400">
                What&apos;s this about?
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`help-category-${id}`}
                    aria-pressed={category === id}
                    onClick={() => setCategory(id)}
                    className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium border transition-colors ${
                      category === id
                        ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]"
                        : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:border-[#00E5FF]/40"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                data-testid="help-name"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]"
              />
              <Input
                data-testid="help-email"
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]"
              />
            </div>

            <Textarea
              data-testid="help-text"
              placeholder={
                category === "technical"
                  ? "What happened? Where in the app did it happen, and what did you expect?"
                  : "Tell us your idea or what would make your study richer."
              }
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              required
              className="rounded-xl border-[#29344D] bg-[#0E131F] resize-none focus-visible:ring-[#00E5FF]/30"
            />

            <Button
              data-testid="help-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              We&apos;ll only use your email to follow up on this message.
            </p>
          </form>
        )}
      </section>

      <p className="text-xs text-slate-500 text-center mt-8">
        See also our <Link to="/privacy" className="underline hover:text-[#00E5FF]">Privacy Policy</Link> and{" "}
        <Link to="/terms" className="underline hover:text-[#00E5FF]">Terms</Link>.
      </p>
    </div>
  );
}
